import type { PollOption } from '@tunasong/schemas'

type OptionNameFields = Pick<PollOption, 'name' | 'entityRef'>

/**
 * `name` is the option's label, written by a person. On a linked option it is the primary text
 * and the linked entity is shown as a secondary reference; the entity itself is never renamed,
 * because it is shared across the library.
 *
 * Linking has always spread the existing option, so text typed before linking is still stored
 * on every linked option. That is why this reuses `name` instead of adding a `label` field:
 * existing polls show their labels with no backfill, and a new field nested in `pollOptions[]`
 * would be stripped by the API until `@tunasong/schemas` deploys there.
 */
const labelOf = (option: OptionNameFields) => option.name.trim()

/** Plain-text name for an option: its label if it has one, else the linked entity's name. */
export const getOptionLabel = (option: OptionNameFields, entityName?: string) => {
  if (option.entityRef && !labelOf(option) && entityName) {
    return entityName
  }
  return option.name
}

/**
 * True when a linked option has a label of its own worth showing above the entity link. A label
 * that just repeats the entity name is not, so the entity is not named twice.
 */
export const hasOwnLabel = (option: OptionNameFields, entityName?: string) => {
  const label = labelOf(option)
  return Boolean(option.entityRef && label && label !== entityName?.trim())
}
